import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalConfig, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { SharedModule } from '@app/shared/shared.module';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { BoxComponent } from '@app/shared/components/liveops/box/box.component';
import { ShopRatesRepository } from '@app/shared/state/shop-rates/shop-rates.repository';
import { RecurringRepository } from '@app/shared/state/recurring/recurring.repository';
import { DisplayComponent } from '@app/shared/components/display/display.component';
import { ShopService } from '@app/shared/services/shop.service';
import { OffersRepository } from '@app/shared/state/offer/offer.repository';

@Component({
  selector: 'slotcom-magicbag-modal',
  templateUrl: './magicbag.component.html',
  styleUrls: ['./magicbag.component.scss'],
  standalone: true,
  imports: [ButtonComponent, BoxComponent, AnimatedTimerComponent, DisplayComponent, SharedModule],
})
export class MagicBagModalComponent implements OnInit {
  readonly event = signal<LimitedTimeEvent | null>(null);
  readonly lang = signal<string>('');

  readonly persistedModalData = signal<ModalConfig | null>(null);
  readonly nonPersistedModalData = signal<Modal | null>(null);

  readonly magicbagMinPurchase = signal<string>('');
  readonly magicbagCurrencySymbol = signal<string>('');
  readonly currentBonus = signal<number>(0);
  readonly rewardBonus = signal<number>(0);

  valueMultiplied = 0;

  private readonly _destroyRef = inject(DestroyRef);
  private readonly eventManager = inject(EventManager);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly translateService = inject(TranslateService);
  private readonly shopRatesRepo = inject(ShopRatesRepository);
  private readonly shopService = inject(ShopService);
  private readonly offersRepo = inject(OffersRepository);
  private readonly recurringRepo = inject(RecurringRepository);
  protected readonly magicBagModalData = inject<Modal | ModalConfig>(MAT_DIALOG_DATA);

  ngOnInit(): void {
    if ('properties' in this.magicBagModalData) {
      this.persistedModalData.set(this.magicBagModalData);
      this.event.set(this.eventsRepo.getEventById(this.magicBagModalData.properties.eventId));
    } else {
      this.nonPersistedModalData.set(this.magicBagModalData);
      this.event.set(this.eventsRepo.getEventById(this.magicBagModalData.data.eventId));
    }

    this.lang.set(this.translateService.currentLang);

    this.recurringRepo.recurringBonus$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((bonus) => {
      this.currentBonus.set(bonus);
      this.rewardBonus.set(bonus * (this.event()?.properties?.magicbagmultiplier ?? 0));
    });

    this.calculateMinPurchase();
  }

  openInfoModal(): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.MagicBagInfoModalComponent, ModalType.View, {
        event: this.event(),
        minPurchase: this.magicbagMinPurchase(),
        currencySymbol: this.magicbagCurrencySymbol(),
        rewardBonus: this.rewardBonus(),
      })
    );
  }

  manageMagicBag(): void {
    this.closeModal();

    const meetsEventMinimumPurchase = this.offersRepo
      .getOffers()
      .filter((offer) => Number(offer.finalPrice.amount) >= Number(this.event()?.properties?.minlocalprice));

    if (meetsEventMinimumPurchase.length > 0) {
      this.shopService.openOfferOrShop(meetsEventMinimumPurchase[0]?.sku);
    } else {
      this.shopService.openShop('Coins');
    }
  }

  closeModal(): void {
    if (this.persistedModalData()) {
      this.eventManager.closeModal(
        new Modal(this.persistedModalData().id, ModalType.Persisted, this.persistedModalData())
      );
    } else {
      this.eventManager.closeModal(
        new Modal(NonPersistedModalName.MagicBagModalComponent, ModalType.View, this.nonPersistedModalData())
      );
    }
  }

  private calculateMinPurchase(): void {
    const magicbagShopItem = this.shopRatesRepo
      .getShopRates()
      .Coins?.find((rate) => rate.sku === this.event()?.properties?.sku);
    this.magicbagMinPurchase.set(magicbagShopItem ? magicbagShopItem.amount : '0');
    this.magicbagCurrencySymbol.set(magicbagShopItem ? magicbagShopItem.currencySymbol : '');
  }
}
